import { ComponentType, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useApolloClient, useLazyQuery } from "@apollo/client";
import isEqual from "fast-deep-equal";
import { DocumentNode } from "graphql";
import { ColumnInstance, Filters, SortingRule } from "react-table";
import { genericMemo } from "../../../../utils";
import { AppConfigContext } from "../../../generic/AppConfigContext";
import { SnackbarMessagePropsExt, useSnackbar } from "../../../ui";
import { Column as BaseColumn, FetchTableData, Table as TableContainer, TableProps, TableSelectionActionProps } from "../../../ui/Table";
import { constructFilter, constructOrder } from "../../builders";
import { FilterExpressionChildInput, FilterExpressionOperatorEnum, FilterOperatorEnum, Maybe, OrderDirectionEnum } from "../../innerTypes";
import { loadData } from "../../loader";
import { FilteringProps, MimicLoaderOptions, OrderDescription, OrderingProps } from "../../types";

export type GraphQLTableColumn<T extends object> = BaseColumn<T> & FilteringProps<T> & OrderingProps<T>

export type GraphQLTableSelectionActionProps = TableSelectionActionProps & {
  filters: FilterExpressionChildInput,
  reFetchTable: () => void
}

export type GraphQLTableProps<T extends object> =
  Omit<TableProps<T>, "data" | "pageCount" | "totalCount" | "loading" | "fetchData" | "defaultColumn" | "selectionActions">
  &
  {
    hideSettings?: boolean,
    query: DocumentNode,
    queryAdditionalProps?: object,
    extractQueryData?: (data: any) => any,
    globalFilters?: Maybe<FilterExpressionChildInput>,
    selectionActions?: (props: GraphQLTableSelectionActionProps) => ReactNode,
    excelLoaderOptions?: MimicLoaderOptions<T>,
    onSetPage?: (pageIndex: number) => void,
    onSetSize?: (pageSize: number) => void,
    onSetSortBy?: (sortBy: SortingRule<T>[]) => void,
    onSetFilters?: (filters: Filters<T>) => void,
    onQueryFilterChange?: (filter: Maybe<FilterExpressionChildInput>) => void,
    onTableDataChanged?: (data: T[]) => void,
  }

export const GraphQLTable = genericMemo(<T extends object>(
  {
    query,
    queryAdditionalProps,
    extractQueryData,
    globalFilters,
    selectionActions,
    excelLoaderOptions,
    onSetFilters,
    onSetSize,
    onSetSortBy,
    onSetPage,
    onTableDataChanged,
    onQueryFilterChange,
    ...restProps
  }: GraphQLTableProps<T>) => {
  const appConfig = useContext(AppConfigContext);
  const apolloClient = useApolloClient();
  const [ page, setPage ] = useState(0);
  const [ size, setSize ] = useState(0);
  const [ countInfo, setCountInfo ] = useState<{ pageCount: number, totalCount: number }>({ pageCount: 0, totalCount: 0 });
  const [ sortBy, setSortBy ] = useState<SortingRule<T>[]>([]);
  const [ filters, setFilters ] = useState<Filters<T>>();
  const [ instanceColumns, setInstanceColumns ] = useState<(ColumnInstance<T> & GraphQLTableColumn<T>)[]>();
  const [ proxiedInstanceColumns, setProxiedInstanceColumns ] = useState<(Partial<ColumnInstance<T> & GraphQLTableColumn<T>>)[]>();
  const defaultColumn = useMemo(
    (): Partial<GraphQLTableColumn<T>> => ({
      Filter: () => null,
      filterOperator: FilterOperatorEnum.Equal,
      filterValueModifier: (v: any) => v,
    }),
    [],
  );

  useEffect(() => {
    const newProxiedColumns = instanceColumns?.map?.(column => ({
      id: column.id,
      orderAccessor: column.orderAccessor,
      filterExpressionBuilder: column.filterExpressionBuilder,
      filterAccessor: column.filterAccessor,
      filterOperator: column.filterOperator,
      filterValueModifier: column.filterValueModifier,
    }));
    if (!isEqual(newProxiedColumns, proxiedInstanceColumns)) {
      setProxiedInstanceColumns(newProxiedColumns);
    }
  }, [ instanceColumns, proxiedInstanceColumns ]);

  const orders = useMemo(() => constructOrder(
    sortBy?.map((
      {
        id,
        desc,
      }) => {
      const column = proxiedInstanceColumns?.find?.(x => x.id === id);

      return ({
        path: (column?.orderAccessor || id) as string,
        direction: desc ? OrderDirectionEnum.Desc : OrderDirectionEnum.Asc,
      }) as OrderDescription<T>;
    }),
  ), [ proxiedInstanceColumns, sortBy ]);
  const queryFilters = useMemo((): Maybe<FilterExpressionChildInput> => {
    const constructedFilter = constructFilter(
      [
        ...filters?.map?.((
          {
            id,
            value,
          }) => {
          const column = proxiedInstanceColumns?.find?.(x => x.id === id);
          const filterValue = column?.filterValueModifier ? column?.filterValueModifier?.(value, appConfig) : value;

          return {
            expressionBuilder: column?.filterExpressionBuilder,
            path: (column?.filterAccessor || id) as string,
            operator: column?.filterOperator === undefined ? (Array.isArray(filterValue) ? FilterOperatorEnum.In : FilterOperatorEnum.Equal) : column?.filterOperator as FilterOperatorEnum,
            // if no column setting just load without any filter
            value: column ? filterValue : undefined,
          };
        }) || [],
      ],
    );

    if (globalFilters) {
      return {
        expr: {
          operator: FilterExpressionOperatorEnum.And,
          children: [
            globalFilters,
            constructedFilter,
          ].filter(x => x),
        },
      };
    }
    return constructedFilter;

  }, [ proxiedInstanceColumns, filters, appConfig, globalFilters ]);

  useEffect(() => {
    onQueryFilterChange?.(queryFilters);
  }, [
    queryFilters, onQueryFilterChange,
  ]);

  const queryOptions = useMemo(() => ({
    variables: {
      ...queryAdditionalProps,
      page,
      size,
      orders,
      filter: queryFilters,
    },
  }), [ page, size, orders, queryFilters, queryAdditionalProps ]);
  const [ fetch, {
    called,
    loading,
    data,
    error,
  } ] = useLazyQuery(query, queryOptions);

  const fetchData = useMemo((): FetchTableData => (
    {
      pageIndex,
      pageSize,
      sortBy,
      filters,
    }) => {
    setPage(pageIndex);
    onSetPage?.(pageIndex);
    setSize(pageSize);
    onSetSize?.(pageSize);
    setSortBy(sortBy);
    onSetSortBy?.(sortBy);
    setFilters(filters);
    onSetFilters?.(filters);
    if (proxiedInstanceColumns) {
      if (!called) {
        fetch();
      }
    }
  }, [ proxiedInstanceColumns, fetch, called, onSetSize, onSetSortBy, onSetPage, onSetFilters ]);

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [ error ]);

  const wrappedSelectionActions = useCallback((props: TableSelectionActionProps) => {
    const { selectedRowIds } = props;
    if (selectionActions) {
      const f: FilterExpressionChildInput = {
        expr: {
          operator: FilterExpressionOperatorEnum.And,
          children: [],
        },
      };
      if (f?.expr?.children) {
        if (selectedRowIds.selectedAll) {
          if (queryFilters) {
            f.expr.children.push(queryFilters);
          }
          const unselectedIds = Object.keys(selectedRowIds.unselected);
          if (unselectedIds.length) {
            f.expr.children.push({
              entry: {
                path: "id",
                operator: FilterOperatorEnum.NotIn,
                value: unselectedIds,
              },
            });
          }
        } else {
          const selectedIds = Object.keys(selectedRowIds.selected);
          if (selectedIds.length) {
            f.expr.children.push({
              entry: {
                path: "id",
                operator: FilterOperatorEnum.In,
                value: selectedIds,
              },
            });
          }
        }
      }
      return selectionActions({
        ...props,
        filters: f,
        reFetchTable: () => fetch(),
      });
    }
  }, [ fetch, selectionActions, queryFilters ]);

  const queryData = extractQueryData?.(error ? data || {} : data);

  useEffect(() => {
    onTableDataChanged?.(queryData?.items);
  }, [ queryData, onTableDataChanged ]);

  useEffect(() => {
    if (queryData) {
      setCountInfo({
        pageCount: queryData.totalPages,
        totalCount: queryData.totalElements,
      });
    }
  }, [ queryData ]);

  const { push } = useSnackbar();
  const toExcelGetItems = useCallback(async (messageProps: SnackbarMessagePropsExt) => {
    return await loadData<T>(query, {
      onProgress: (e => {
        push({
          type: "info",
          ...messageProps,
          message: `Loaded ${e.loadedItems}/${e.totalItems}`,
        });
      }),
      client: apolloClient,
      ...excelLoaderOptions,
      filter: queryFilters,
      queryAdditionalProps: queryAdditionalProps,
      orders,
    });
  }, [ query, orders, queryFilters, excelLoaderOptions, apolloClient, push, queryAdditionalProps ]);

  return <TableContainer
    data={queryData?.items || []}
    pageCount={countInfo.pageCount}
    totalCount={countInfo.totalCount}
    loading={!called || loading}
    fetchData={fetchData}
    defaultColumn={defaultColumn}
    selectionActions={wrappedSelectionActions}
    toExcelGetItems={toExcelGetItems}
    onColumnsChanged={setInstanceColumns}
    {...restProps}
  />;
});

(GraphQLTable as ComponentType<GraphQLTableProps<any>>).defaultProps = {
  extractQueryData: (data) => Object.values(data || {})?.[0],
};


export default GraphQLTable;
